import './styles.css';
import { errorMessage } from '@runnerboy/api-client';
import { resources } from '@runnerboy/i18n';
import { Alert, Badge, Button, Card, cx, DemoRibbon, Field, Input, LogoMark, Select, ToastProvider, Wordmark } from '@runnerboy/ui';
import i18n from 'i18next';
import { BarChart3, Boxes, ClipboardList, LifeBuoy, LogOut, Store, Wallet, Wifi, WifiOff } from 'lucide-react';
import { StrictMode, useState, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { initReactI18next } from 'react-i18next';
import { BrowserRouter, NavLink, Route, Routes } from 'react-router';
import { registerSW } from 'virtual:pwa-register';
import { OrdersPage } from './orders';
import { AnalyticsPage, InventoryPage, SettlementsPage, StoreProfilePage, StoreSupportPage } from './pages';
import { client, StoreSessionProvider, useStoreSession } from './session';

void i18n.use(initReactI18next).init({ resources, lng: 'en', fallbackLng: 'en', interpolation: { escapeValue: false } });

function Login() {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testMode, setTestMode] = useState(false);
  return (
    <div className="grid min-h-screen place-items-center bg-ink-900 px-4">
      <Card className="w-full max-w-sm p-6">
        <Wordmark />
        <h1 className="mt-6 text-xl font-extrabold">Partner store sign in</h1>
        <p className="mt-1 text-sm text-ink-500">For staff of licensed retailers on Runner Boy.</p>
        <form
          className="mt-5 flex flex-col gap-4"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError(null);
            try {
              if (step === 'phone') {
                const r = await client.requestOtp(phone);
                setTestMode(!!r.testMode);
                setStep('code');
              } else await client.verifyOtp(phone, code, 'Store tablet');
            } catch (err) {
              setError(errorMessage(err));
            } finally {
              setBusy(false);
            }
          }}
        >
          {step === 'phone' ? (
            <Field label="Registered mobile number" htmlFor="ph">
              <Input id="ph" inputMode="numeric" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} autoFocus />
            </Field>
          ) : (
            <Field label="6-digit code" htmlFor="code">
              <Input id="code" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} autoFocus />
            </Field>
          )}
          {testMode && <Alert tone="warning">Test environment: no SMS is sent. Use the demo code given to you by the Runner Boy team.</Alert>}
          {error && <Alert tone="danger">{error}</Alert>}
          <Button type="submit" size="lg" loading={busy} disabled={step === 'phone' ? phone.length !== 10 : code.length !== 6}>
            {step === 'phone' ? 'Send code' : 'Sign in'}
          </Button>
        </form>
      </Card>
    </div>
  );
}

const NAV = [
  { to: '/', label: 'Orders', icon: ClipboardList, end: true },
  { to: '/inventory', label: 'Inventory', icon: Boxes },
  { to: '/analytics', label: 'Analytics', icon: BarChart3 },
  { to: '/settlements', label: 'Settlements', icon: Wallet },
  { to: '/store', label: 'Store', icon: Store },
  { to: '/support', label: 'Support', icon: LifeBuoy },
];

function Shell({ children }: { children: ReactNode }) {
  const { store, stores, selectStore, connected, user } = useStoreSession();
  return (
    <div className="min-h-screen pb-20 lg:pb-0 lg:pl-60">
      <aside className="fixed inset-y-0 left-0 hidden w-60 flex-col bg-ink-900 p-4 text-white lg:flex">
        <Wordmark inverted size="sm" />
        <nav className="mt-8 flex flex-col gap-1" aria-label="Store">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => cx('flex h-11 items-center gap-3 rounded-[12px] px-3 text-sm font-semibold', isActive ? 'bg-white/10 text-white' : 'text-ink-300 hover:text-white')}>
              <n.icon className="h-5 w-5" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto text-xs text-ink-400">{user?.phone}</div>
        <button onClick={() => void client.logout()} className="mt-2 flex items-center gap-2 text-sm text-ink-300 hover:text-white">
          <LogOut className="h-4 w-4" /> Log out
        </button>
      </aside>
      <header className="sticky top-0 z-20 border-b border-ink-100 bg-paper/95 backdrop-blur">
        <div className="flex items-center gap-3 px-4 py-3">
          {/* Phones show only the mark so the store name stays readable; tablets get the full wordmark. */}
          <div className="shrink-0 sm:hidden">
            <LogoMark size={32} />
          </div>
          <div className="hidden shrink-0 sm:block lg:hidden">
            <Wordmark size="sm" />
          </div>
          {stores.length > 1 ? (
            <Select aria-label="Store" value={store?.id ?? ''} onChange={(e) => selectStore(e.target.value)} className="h-10 max-w-xs">
              {stores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          ) : (
            <div className="min-w-0 truncate font-bold">{store?.name}</div>
          )}
          {store && <Badge tone={store.operationalState === 'OPEN' ? 'success' : store.operationalState === 'PAUSED' ? 'warning' : 'danger'}>{store.operationalState}</Badge>}
          <span className={cx('ml-auto inline-flex items-center gap-1 text-xs font-semibold', connected ? 'text-success-600' : 'text-danger-600')}>
            {connected ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
            {connected ? 'Live' : 'Offline'}
          </span>
        </div>
        {store?.isDemo && <DemoRibbon />}
      </header>
      <main className="mx-auto max-w-6xl p-4">{children}</main>
      <nav className="rb-safe-bottom fixed inset-x-0 bottom-0 z-20 grid grid-cols-6 border-t border-ink-100 bg-white lg:hidden" aria-label="Store">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => cx('flex h-16 flex-col items-center justify-center gap-1 text-[10px] font-semibold', isActive ? 'text-peacock-700' : 'text-ink-500')}>
            <n.icon className="h-5 w-5" /> {n.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

function App() {
  const { ready, user, store, stores } = useStoreSession();
  if (!ready) return null;
  if (!user) return <Login />;
  if (stores.length > 0 && !store) return null;
  return (
    <Shell>
      <Routes>
        <Route path="/" element={<OrdersPage />} />
        <Route path="/inventory" element={<InventoryPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/settlements" element={<SettlementsPage />} />
        <Route path="/store" element={<StoreProfilePage />} />
        <Route path="/support" element={<StoreSupportPage />} />
      </Routes>
    </Shell>
  );
}

registerSW({ immediate: true });
createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <ToastProvider>
      <StoreSessionProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </StoreSessionProvider>
    </ToastProvider>
  </StrictMode>,
);
